// process.env safe client component
import React, { useState, useEffect } from 'react';
import SimpleModeApp from './simple/SimpleModeApp';
import SetupChoiceModal from './simple/SetupChoiceModal';
import {
  Menu, 
  Bot, Globe, Sparkles, Database, Shield, Layers, User, LogOut,
  ExternalLink, Copy, Check, AlertCircle, RefreshCw, Cpu, Activity,
  Smartphone, Terminal, CheckCircle2, ShieldCheck, Zap, Layout, Code, Eye,
  RotateCcw, Clock, ChevronRight, X, History, ArrowDownCircle, ArrowUpCircle,
  ChevronDown, ChevronUp, CreditCard, Crown, Share2
} from 'lucide-react';
import { TenetDynamicWorkspace } from './TenetDynamicWorkspace';
import { TenetPortalBuilder } from './TenetPortalBuilder';
import { TenetPortalData } from './TenetPortalData';
import { VersionUpdateNotifier } from '../common/VersionUpdateNotifier';
import TenetAccountBillingModal from './TenetAccountBillingModal';

interface TenetDashboardProps {
  token: string;
  initialTenant?: any;
  onLogout: () => void;
}

// ── Canonical Semver Version Helper ──
function parseSemver(v: string): number[] {
  const clean = (v || '').replace(/^[^\d]*/, '');
  return clean.split('.').map(n => parseInt(n, 10) || 0);
}

function compareSemver(vA: string, vB: string): number {
  if (!vA || !vB) return 0;
  const a = parseSemver(vA);
  const b = parseSemver(vB);
  for (let i = 0; i < Math.max(a.length, b.length); i++) {
    const numA = a[i] || 0;
    const numB = b[i] || 0;
    if (numA > numB) return 1;
    if (numA < numB) return -1;
  }
  return 0;
}

export default function TenetAdminDashboard({ token, initialTenant, onLogout }: TenetDashboardProps) {
  const [tenant, setTenant] = useState<any>(initialTenant || null);
  const [mobileNavOpen, setMobileNavOpen] = useState<boolean>(false);
  const [activeTab, setActiveTab] = useState<'workspace' | 'portal_builder' | 'portal_data' | 'billing'>(() => {
    if (typeof window !== 'undefined') {
      const params = new URLSearchParams(window.location.search);
      if (params.has('payment_success') || params.get('payment') === 'success') {
        return 'billing';
      }
    }
    return 'workspace';
  });
  const [selectedPortalNode, setSelectedPortalNode] = useState<string | null>(null);
  const [selectedPortalNodeId, setSelectedPortalNodeId] = useState<string | null>(null);
  const [hasWhatsAppNode, setHasWhatsAppNode] = useState<boolean>(false);
  const [copiedKey, setCopiedKey] = useState<boolean>(false);
  const [toastMessage, setToastMessage] = useState<string | null>(null);

  // ✅ Phase 5: Simple Mode (default for every user) ↔ Complete Dashboard switch.
  // The complete dashboard is never removed — one tap switches back.
  const [uiMode, setUiMode] = useState<'simple' | 'complete'>(() => {
    if (typeof window === 'undefined') return 'simple';
    return localStorage.getItem('tenet_ui_mode') === 'complete' ? 'complete' : 'simple';
  });
  // ✅ New accounts MUST see the setup choice first (WhatsApp Agent vs Custom) —
  // shown as a popup on any tab/mode until chosen. Dismiss-equivalent = 'other'.
  const [setupChoice, setSetupChoice] = useState<string | null | undefined>(undefined);
  useEffect(() => {
    // v6.13.71: MUST send the auth header — without it requireTenetAuth returns 401,
    // the response has no `choice`, and the popup re-opened on EVERY login (the bug).
    const hdrs: Record<string, string> = { 'Cache-Control': 'no-cache' };
    try {
      const t = token || (typeof window !== 'undefined' ? localStorage.getItem('tenet_auth_token') : '') || '';
      if (t) hdrs['Authorization'] = t.startsWith('Bearer ') ? t : `Bearer ${t}`;
    } catch { /* private mode */ }
    fetch('/api/tenet/onboarding/setup-choice', { headers: hdrs, cache: 'no-store' })
      .then(async (r) => ({ ok: r.ok, j: await r.json().catch(() => null) }))
      .then(({ ok, j }) => {
        if (!ok) { setSetupChoice('other'); return; } // never nag on failed reads
        setSetupChoice((j && typeof j.choice === 'string' && j.choice) ? j.choice : null);
      })
      .catch(() => setSetupChoice('other')); // never block the dashboard on network errors
  }, [token]);

  // 🚫 v6.13.71: ACCOUNT LOCK — after login the ONLY thing this account sees is a friendly
  // "contact the owner" screen (server also 403s every API + pauses the WhatsApp agent).
  const [accountLock, setAccountLock] = useState<{ message?: string | null; phone?: string | null } | null>(null);
  useEffect(() => {
    const t = token || (typeof window !== 'undefined' ? localStorage.getItem('tenet_auth_token') : '') || '';
    if (!t) return;
    fetch('/api/tenet/me', { cache: 'no-store', headers: { 'Authorization': t.startsWith('Bearer ') ? t : `Bearer ${t}` } })
      .then((r) => r.json())
      .then((d) => { if (d?.accountLocked) setAccountLock({ message: d.lockMessage, phone: d.lockContactPhone }); })
      .catch(() => {});
  }, [token]);

  const switchUiMode = (mode: 'simple' | 'complete') => {
    try { localStorage.setItem('tenet_ui_mode', mode); } catch { /* private mode */ }
    setUiMode(mode);
  };

  // ✅ Phase 1: 2-Step Confirmation Protocol — live Approvals panel.
  // The dashboard runs the SAME protocol as WhatsApp: every owner mutation shows a
  // preview here with Confirm / Cancel, and executed actions keep a 24h Undo.
  const [pendingActions, setPendingActions] = useState<any[]>([]);
  const [recentActions, setRecentActions] = useState<any[]>([]);
  const [showApprovals, setShowApprovals] = useState<boolean>(false);
  const [approvalsResult, setApprovalsResult] = useState<string | null>(null);

  const refreshApprovals = React.useCallback(async () => {
    try {
      const h: Record<string, string> = {};
      const t = token || (typeof window !== 'undefined' ? localStorage.getItem('tenet_auth_token') : null);
      if (t) h['Authorization'] = t.startsWith('Bearer ') ? t : `Bearer ${t}`;
      const r = await fetch('/api/tenet/pending-actions', { headers: h, cache: 'no-store' });
      const j = await r.json();
      if (j?.success) {
        setPendingActions(Array.isArray(j.active) ? j.active : []);
        setRecentActions(Array.isArray(j.recent) ? j.recent : []);
      }
    } catch { /* offline — silently keep last known state */ }
  }, [token]);

  useEffect(() => {
    refreshApprovals();
    const iv = setInterval(refreshApprovals, 20000);
    return () => clearInterval(iv);
  }, [refreshApprovals]);

  const runApprovalAction = async (id: number | string, kind: 'confirm' | 'cancel' | 'undo') => {
    try {
      const h: Record<string, string> = { 'Content-Type': 'application/json' };
      const t = token || (typeof window !== 'undefined' ? localStorage.getItem('tenet_auth_token') : null);
      if (t) h['Authorization'] = t.startsWith('Bearer ') ? t : `Bearer ${t}`;
      const r = await fetch(`/api/tenet/pending-actions/${id}/${kind}`, { method: 'POST', headers: h });
      const j = await r.json();
      setApprovalsResult(j?.resultText || (j?.success ? '✅ කළා.' : '❌ ' + (j?.error || 'failed')));
      setTimeout(() => setApprovalsResult(null), 6000);
      await refreshApprovals();
    } catch {
      setApprovalsResult('❌ network error');
      setTimeout(() => setApprovalsResult(null), 4000);
    }
  };
  const [isBillingModalOpen, setIsBillingModalOpen] = useState<boolean>(false);
  const [billingInitialTab, setBillingInitialTab] = useState<'plans' | 'bank_transfer' | 'invoices' | 'token_history'>('plans');

  useEffect(() => {
    const handler = (e: any) => {
      if (e.detail?.tab) {
        setBillingInitialTab(e.detail.tab);
      }
      setActiveTab('billing');
    };
    window.addEventListener('open-tenet-billing-tab', handler);
    return () => window.removeEventListener('open-tenet-billing-tab', handler);
  }, []);

  // System Release & Version Governance (Enterprise SaaS Standard)
  const [systemRelease, setSystemRelease] = useState<any>(null);
  const [releaseHistory, setReleaseHistory] = useState<any[]>([]);
  const activeTenantId = tenant?.id || initialTenant?.id;
  const tenantStorageKey = activeTenantId ? `kangavena_running_version_tenant_${activeTenantId}` : 'kangavena_running_version';
  const activeToken = token || tenant?.apiKey || (typeof window !== 'undefined' ? localStorage.getItem('tenet_auth_token') || '' : '');

  const [showVersionModal, setShowVersionModal] = useState<boolean>(false);
  const [isUpdatingRelease, setIsUpdatingRelease] = useState<boolean>(false);
  const [expandedReleaseId, setExpandedReleaseId] = useState<number | string | null>(null);
  const [runningVersion, setRunningVersion] = useState<string>(() => {
    const tid = initialTenant?.id;
    const key = tid ? `kangavena_running_version_tenant_${tid}` : 'kangavena_running_version';
    return (
      initialTenant?.customFields?.systemVersion ||
      (typeof window !== 'undefined' && sessionStorage.getItem(key)) ||
      'v6.9.1'
    );
  });

  // An update is ONLY available if systemRelease.version is strictly GREATER than runningVersion
  const isUpdateAvailable = Boolean(
    systemRelease?.version &&
    compareSemver(systemRelease.version, runningVersion) > 0
  );

  const fetchReleaseData = async () => {
    try {
      const isBeta = typeof window !== 'undefined' && localStorage.getItem('kangavena_beta_preview') === '1';
      const activeToken = token || tenant?.apiKey || (typeof window !== 'undefined' && localStorage.getItem('tenet_auth_token'));
      const headers: Record<string, string> = { 'Cache-Control': 'no-cache' };
      if (isBeta) headers['x-beta-preview'] = '1';
      if (activeToken) {
        headers['Authorization'] = activeToken.startsWith('Bearer ') ? activeToken : `Bearer ${activeToken}`;
        if (activeToken.startsWith('TENET_')) headers['x-tenant-api-key'] = activeToken;
      }

      const [resActive, resHistory] = await Promise.all([
        fetch('/api/system/releases/active', { headers, cache: 'no-store' }).then(r => r.json()).catch(() => null),
        fetch('/api/system/releases/history', { headers, cache: 'no-store' }).then(r => r.json()).catch(() => null),
      ]);

      if (resActive?.success && resActive?.version) {
        setSystemRelease(resActive);
        if (resActive.tenantRunningVersion) {
          setRunningVersion(resActive.tenantRunningVersion);
          sessionStorage.setItem(tenantStorageKey, resActive.tenantRunningVersion);
        } else if (typeof window !== 'undefined') {
          const stored = sessionStorage.getItem(tenantStorageKey);
          if (stored) {
            setRunningVersion(stored);
          } else if (tenant?.customFields?.systemVersion) {
            setRunningVersion(tenant.customFields.systemVersion);
            sessionStorage.setItem(tenantStorageKey, tenant.customFields.systemVersion);
          } else {
            // Pin to current initial version so tenant NEVER auto-jumps to future releases
            const initialVer = resActive.version;
            setRunningVersion(initialVer);
            sessionStorage.setItem(tenantStorageKey, initialVer);

            // Auto-persist to this tenant's customFields in DB
            fetch('/api/system/releases/tenant-set-version', {
              method: 'POST',
              headers,
              body: JSON.stringify({ version: initialVer, tenantId: activeTenantId }),
            }).catch(() => {});
          }
        }
      }
      if (resHistory?.success && Array.isArray(resHistory?.releases)) {
        setReleaseHistory(resHistory.releases);
      }
    } catch (_) {}
  };

  useEffect(() => {
    fetchReleaseData();
    const interval = setInterval(fetchReleaseData, 30000);
    return () => clearInterval(interval);
  }, [activeTenantId, tenantStorageKey]);

  const handleApplyUpdate = async (targetVer?: string) => {
    const versionToSet = targetVer || systemRelease?.version;
    if (!versionToSet) return;
    setIsUpdatingRelease(true);

    try {
      const activeToken = token || tenant?.apiKey || localStorage.getItem('tenet_auth_token');
      const headers: Record<string, string> = { 'Content-Type': 'application/json' };
      if (activeToken) {
        headers['Authorization'] = activeToken.startsWith('Bearer ') ? activeToken : `Bearer ${activeToken}`;
        if (activeToken.startsWith('TENET_')) headers['x-tenant-api-key'] = activeToken;
      }

      await fetch('/api/system/releases/tenant-set-version', {
        method: 'POST',
        headers,
        body: JSON.stringify({ version: versionToSet, tenantId: activeTenantId }),
      }).catch(() => {});
    } catch (_) {}

    if (typeof window !== 'undefined') {
      sessionStorage.setItem(tenantStorageKey, versionToSet);
    }
    showToast(`Applying update to ${versionToSet}...`);
    setTimeout(() => {
      window.location.reload();
    }, 400);
  };

  const handleRollbackVersion = async (targetVer: string) => {
    const isUpgrade = compareSemver(targetVer, runningVersion) > 0;
    const actionName = isUpgrade ? 'update to' : 'rollback to';
    if (!confirm(`Switch system version to ${targetVer}?\nYour active tenant session will reload using the ${targetVer} release checkpoint.`)) {
      return;
    }
    try {
      const activeToken = token || tenant?.apiKey || localStorage.getItem('tenet_auth_token');
      const headers: Record<string, string> = { 'Content-Type': 'application/json' };
      if (activeToken) {
        headers['Authorization'] = activeToken.startsWith('Bearer ') ? activeToken : `Bearer ${activeToken}`;
        if (activeToken.startsWith('TENET_')) headers['x-tenant-api-key'] = activeToken;
      }

      await fetch('/api/system/releases/tenant-set-version', {
        method: 'POST',
        headers,
        body: JSON.stringify({ version: targetVer, tenantId: activeTenantId }),
      }).catch(() => {});
    } catch (_) {}

    if (typeof window !== 'undefined') {
      sessionStorage.setItem(tenantStorageKey, targetVer);
    }
    showToast(`Switched system version to ${targetVer}`);
    setTimeout(() => {
      window.location.reload();
    }, 400);
  };

  const isImpersonated = typeof window !== 'undefined' && (
    sessionStorage.getItem('tenet_is_impersonated') === '1' ||
    localStorage.getItem('tenet_is_impersonated') === '1' ||
    new URLSearchParams(window.location.search).get('impersonated') === '1'
  );

  const showToast = (msg: string) => {
    setToastMessage(msg);
    setTimeout(() => setToastMessage(null), 3000);
  };

  const copyToClipboard = (text: string, label: string = 'API Key') => {
    if (!text) return;
    navigator.clipboard.writeText(text);
    setCopiedKey(true);
    showToast(`${label} copied to clipboard!`);
    setTimeout(() => setCopiedKey(false), 2500);
  };

  // Fetch or refresh tenant profile metadata if needed
  useEffect(() => {
    if (!tenant && token) {
      fetch('/api/tenet/me', {
        headers: {
          'Authorization': token.startsWith('Bearer ') ? token : `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })
      .then(async res => {
        const contentType = res.headers.get('content-type');
        if (!res.ok || !contentType || !contentType.includes('application/json')) {
          throw new Error(`Invalid API response format: ${res.status}`);
        }
        return res.json();
      })
      .then(data => {
        if (data.success && data.tenant) {
          setTenant(data.tenant);
        }
      })
      .catch(err => console.warn('[TenetDashboard] Profile fetch warning:', err));
    }
  }, [token, tenant]);

  // Dynamically synchronize WhatsApp Web visibility with active visual flow nodes
  useEffect(() => {
    if (!token) return;
    const activeToken = token.startsWith('Bearer ') ? token : `Bearer ${token}`;
    fetch('/api/tenet/portal-data', {
      headers: { 'Authorization': activeToken }
    })
      .then(r => r.json())
      .then(data => {
        if (data.success && Array.isArray(data.activeFlowNodes)) {
          const hasWA = data.activeFlowNodes.some((n: string) => 
            n.toLowerCase().includes('whatsapp') || n.includes('WHATSAPP')
          );
          setHasWhatsAppNode(hasWA);
          if (!hasWA && selectedPortalNode === 'whatsapp_web') {
            setSelectedPortalNode(null);
          }
        }
      })
      .catch(err => console.warn('[TenetDashboard] Active flow nodes check:', err));
  }, [token, selectedPortalNode]);


  if (accountLock) {
    return (
      <div className="lg-bg" style={{ minHeight: '100dvh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 22 }}>
        <div className="liquid-card-3d" style={{ maxWidth: 420, width: '100%', padding: '30px 22px', textAlign: 'center' }}>
          <div style={{ fontSize: 42 }}>🌙</div>
          <div style={{ fontWeight: 900, fontSize: 19, color: '#E6F4FF', marginTop: 10 }}>Account Paused</div>
          <div style={{ fontSize: 13, color: '#A9C7E8', marginTop: 12, lineHeight: 1.7 }}>
            {accountLock.message || 'මේ account එක තාවකාලිකව නවත්තලා තියෙනවා. භාවිතා කරන්න කලින් කරුණාකර අපගේ හිමිකරු අමතන්න.'}
            <br /><br />
            This account is paused. Please contact the owner before using it — a quick friendly chat and you are good to go.
          </div>
          {accountLock.phone && (
            <a
              href={`tel:${accountLock.phone.replace(/\s/g, '')}`}
              className="liquid-card-3d"
              style={{ display: 'block', marginTop: 18, padding: '13px 16px', fontWeight: 800, fontSize: 15, color: '#04121F', background: 'linear-gradient(135deg,#38BDF8,#22D3EE)', borderRadius: 16, textDecoration: 'none' }}
            >
              📞 {accountLock.phone}
            </a>
          )}
          <div style={{ fontSize: 11, color: '#64748B', marginTop: 16 }}>Kangavena — හිමිකරු සමඟ කතා කිරීමෙන් පසු ගිණුම සැණින් සක්‍රීය වේ.</div>
        </div>
      </div>
    );
  }

  if (uiMode === 'simple') {
    return (
      <>
        {setupChoice === null && (
          <SetupChoiceModal token={token} onChoose={(choice) => { setSetupChoice(choice); if (choice !== 'whatsapp_agent') return; /* stay in simple — tour runs */ }} />
        )}
        <SimpleModeApp token={token} onSwitchToComplete={() => switchUiMode('complete')} />
      </>
    );
  }

  return (
    <>
    {setupChoice === null && (
      <SetupChoiceModal token={token} onChoose={(choice) => { setSetupChoice(choice); if (choice === 'whatsapp_agent') switchUiMode('simple'); }} />
    )}
    <div className="lg-bg h-[100dvh] min-h-[100dvh] w-full bg-[#090D16] text-slate-100 font-sans flex flex-col md:flex-row selection:bg-cyan-500/30 selection:text-cyan-200 antialiased overflow-hidden">
      
      {/* Global Toast Notification */}
      {toastMessage && (
        <div className="tenet-toast fixed top-5 right-5 z-[100] bg-[#121826]/95 border border-cyan-500/40 text-cyan-200 px-4 py-3 rounded-xl shadow-2xl backdrop-blur-xl flex items-center gap-3 animate-in fade-in slide-in-from-top-3">
          <Sparkles className="w-4 h-4 text-cyan-400 animate-spin" />
          <span className="text-xs font-sans font-medium tracking-wide">{toastMessage}</span>
        </div>
      )}

      {/* ═════════════════════════════════════════════════════════════════ */}
      {/* SOVEREIGN EXECUTIVE SIDEBAR (KANGAVENA VIP OBSIDIAN HUD)          */}
      {/* ═════════════════════════════════════════════════════════════════ */}
      <aside className={`lg-glass tenet-dash-aside w-full md:w-64 bg-gradient-to-b from-[#080B14] via-[#0A0E1A] to-[#06080F] border-r border-white/[0.08] flex flex-col justify-between shrink-0 p-3.5 md:h-screen sticky top-0 z-30 shadow-[4px_0_30px_rgba(0,0,0,0.7)] ${mobileNavOpen ? 'menu-open' : ''}`}>
        <div className="space-y-3.5 overflow-y-auto pr-0.5 flex-1 custom-scrollbar [&::-webkit-scrollbar]:w-1 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:bg-white/10 hover:[&::-webkit-scrollbar-thumb]:bg-cyan-500/40 [&::-webkit-scrollbar-thumb]:rounded-full [scrollbar-width:thin] [scrollbar-color:rgba(255,255,255,0.1)_transparent]">
          
          {/* Top Brand Logo - Sovereign Luxury Emblem */}
          <div className="tenet-brand flex items-center gap-2.5 pb-3 border-b border-white/[0.08]">
            <div className="relative p-2 rounded-xl bg-gradient-to-br from-cyan-500/20 via-slate-900 to-indigo-950/40 border border-cyan-500/40 text-cyan-400 shadow-[0_0_20px_rgba(6,182,212,0.35)] shrink-0">
              <Bot className="w-5 h-5" />
              <span className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-emerald-400 rounded-full border-2 border-[#080B14] shadow-[0_0_8px_rgba(52,211,153,0.8)] animate-pulse" />
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-1.5">
                <span className="text-white font-black text-sm tracking-tight font-sans bg-gradient-to-r from-white via-slate-200 to-slate-400 bg-clip-text text-transparent">
                  KANGAVENA
                </span>
                <span className="text-[8.5px] font-mono font-bold px-1.5 py-0.5 rounded bg-gradient-to-r from-amber-500/20 to-amber-600/20 text-amber-300 border border-amber-500/40 shadow-sm shrink-0">
                  TENET
                </span>
              </div>
              <p className="text-[9.5px] font-mono text-cyan-400/80 tracking-wider font-semibold truncate">SOVEREIGN WORKSPACE</p>
            </div>
            <button
              type="button"
              onClick={() => setMobileNavOpen(v => !v)}
              aria-label="Toggle dashboard menu"
              className="tenet-menu-toggle ml-auto p-2 rounded-xl bg-white/[0.04] border border-white/[0.08] text-slate-300 hover:text-white hover:border-cyan-400/50 transition cursor-pointer"
            >
              {mobileNavOpen ? <X size={18} /> : <Menu size={18} />}
            </button>
          </div>

          {/* God-Mode Impersonation Indicator */}
          {isImpersonated && (
            <div className="bg-gradient-to-r from-purple-950/90 via-indigo-950/90 to-slate-950 border border-purple-500/50 rounded-xl p-2.5 shadow-[0_0_20px_rgba(168,85,247,0.2)] flex items-center justify-between gap-2 animate-in fade-in">
              <div className="flex items-center gap-2 min-w-0">
                <span className="flex h-2 w-2 relative shrink-0">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-purple-400 opacity-75" />
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-purple-500" />
                </span>
                <span className="text-[10px] font-mono font-bold text-purple-300 uppercase tracking-tight truncate flex items-center gap-1">
                  <Eye size={11} className="text-purple-400" />
                  God-Mode Active
                </span>
              </div>
              <button
                onClick={() => {
                  sessionStorage.removeItem('tenet_is_impersonated');
                  localStorage.removeItem('tenet_is_impersonated');
                  window.close();
                }}
                className="text-[9px] font-mono px-2 py-0.5 rounded bg-purple-500/20 hover:bg-purple-500/40 text-purple-200 border border-purple-500/30 transition cursor-pointer shrink-0 font-bold"
              >
                Close
              </button>
            </div>
          )}

          {/* Tenant Identity Card - Executive Obsidian Glass */}
          <div className="tenet-drawer-item bg-gradient-to-b from-[#111728] via-[#0D1220] to-[#090D17] border border-white/[0.08] hover:border-cyan-500/40 rounded-xl p-2.5 space-y-1.5 transition-all shadow-md group">
            <div className="flex items-center justify-between">
              <span className="text-[9.5px] font-bold uppercase tracking-wider text-slate-400 font-mono flex items-center gap-1.5">
                <Shield size={11} className="text-cyan-400" />
                <span>TENANT CLIENT</span>
              </span>
              <span className="text-[8.5px] font-bold font-mono px-1.5 py-0.5 rounded-full bg-emerald-500/15 text-emerald-300 border border-emerald-500/30 flex items-center gap-1 shadow-[0_0_8px_rgba(16,185,129,0.2)] shrink-0">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" /> LIVE
              </span>
            </div>
            <p className="text-xs font-black text-white tracking-tight truncate">{tenant?.name || 'Enterprise Client'}</p>
            
            {tenant?.url ? (
              <p className="text-[10.5px] text-slate-400 font-sans truncate flex items-center gap-1.5 pt-0.5">
                <span className="w-1.5 h-1.5 rounded-full bg-cyan-400 shrink-0" />
                <a 
                  href={tenant.url.startsWith('http') ? tenant.url : `https://${tenant.url}`} 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="hover:text-cyan-300 transition truncate flex items-center gap-1 font-mono text-[9.5px] text-slate-300"
                >
                  {tenant.url} <ExternalLink size={8} />
                </a>
              </p>
            ) : (
              <span className="text-[9.5px] text-slate-500 font-medium flex items-center gap-1 font-mono">
                <Globe size={10} /> Cloud Deployment
              </span>
            )}
          </div>

          {/* Account Plan & Token HUD (VIP Sovereign Black Card) */}
          <div
            onClick={() => {
              setActiveTab('billing');
              setMobileNavOpen(false);
            }}
            className="tenet-drawer-item bg-gradient-to-br from-[#14121d] via-[#0e1322] to-[#0a0e19] border border-amber-500/35 hover:border-amber-400/80 rounded-xl p-2.5 transition-all cursor-pointer group shadow-[0_4px_20px_rgba(0,0,0,0.5)] hover:shadow-[0_0_25px_rgba(245,158,11,0.25)] relative overflow-hidden"
          >
            {/* Row 1: Plan Tier & Badge */}
            <div className="flex items-center justify-between gap-1 pb-1.5 border-b border-white/[0.06]">
              <div className="flex items-center gap-1.5 min-w-0">
                <Crown size={12} className="text-amber-400 shrink-0 group-hover:rotate-12 transition-transform drop-shadow-[0_0_8px_rgba(245,158,11,0.6)]" />
                <span className="text-[10px] font-bold uppercase tracking-wider text-amber-300 font-mono truncate">
                  {tenant?.planType === 'pro' || tenant?.planType === 'enterprise' ? 'PRO UNLIMITED' : 'BASIC TRIAL'}
                </span>
              </div>
              <span className={`text-[8.5px] font-mono font-bold px-1.5 py-0.5 rounded-full border shrink-0 ${
                tenant?.isWhiteLabelEnabled 
                  ? 'bg-emerald-500/20 text-emerald-300 border-emerald-500/40 shadow-[0_0_8px_rgba(16,185,129,0.2)]' 
                  : 'bg-amber-500/20 text-amber-300 border-amber-500/40'
              }`}>
                {tenant?.isWhiteLabelEnabled ? 'WHITE-LABEL' : 'UPGRADE'}
              </span>
            </div>
            
            {/* Row 2: Tokens & Manage Link */}
            <div className="flex items-center justify-between gap-1 pt-1.5">
              <div className="flex items-center gap-1.5 text-[10.5px] font-mono font-bold min-w-0">
                {tenant?.planType === 'pro' || tenant?.planType === 'enterprise' ? (
                  <div className="flex items-center gap-1 text-emerald-400 font-bold truncate">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse shrink-0" />
                    <span className="truncate">Uncapped Tokens</span>
                  </div>
                ) : (
                  <div className="truncate text-white">
                    {(tenant?.tokenBalance ?? 100000).toLocaleString()}{' '}
                    <span className="text-slate-400 text-[9.5px] font-normal font-sans">tok</span>
                  </div>
                )}
              </div>
              <span className="text-[9.5px] font-mono text-amber-300/90 group-hover:text-amber-200 flex items-center gap-0.5 font-bold shrink-0 transition-colors">
                <span>Manage</span> <ChevronRight size={9} />
              </span>
            </div>
          </div>

          {/* Navigation Tabs (Flow Graph vs Portal Data vs Portal Builder vs Account) */}
          <div className="tenet-mobile-nav space-y-1.5 pt-1">
            <span className="tenet-nav-head text-[10px] font-mono uppercase tracking-wider text-slate-400 font-bold px-1 block flex items-center justify-between">
              <span>STUDIO APPS</span>
              <span className="text-[9px] text-cyan-500 font-normal">v6.11</span>
            </span>
            
            {/* 1. Flow Runtime Graph */}
            <button
              type="button"
              onClick={() => {
                setActiveTab('workspace');
                setMobileNavOpen(false);
              }}
              className={`w-full px-3 py-2.5 rounded-xl text-xs font-bold transition-all flex items-center justify-between cursor-pointer border ${
                activeTab === 'workspace'
                  ? 'bg-gradient-to-r from-cyan-500/20 via-cyan-500/10 to-[#0B101C]/50 border-cyan-400/80 text-cyan-200 shadow-[0_0_20px_rgba(6,182,212,0.3)] ring-1 ring-cyan-500/30'
                  : 'bg-[#0c101c]/70 border-white/[0.06] text-slate-400 hover:text-white hover:bg-white/[0.04] hover:border-white/[0.14]'
              }`}
            >
              <div className="flex items-center gap-2.5">
                <Layers size={15} className={activeTab === 'workspace' ? 'text-cyan-400' : 'text-slate-400'} />
                <span className="tenet-nav-label-full">Flow Runtime Graph</span>
                <span className="tenet-nav-label-short">Flow</span>
              </div>
              <span className="tenet-nav-badge w-2 h-2 rounded-full bg-emerald-400 shadow-[0_0_8px_rgba(52,211,153,0.8)] animate-pulse" />
            </button>

            {/* 2. Portal Data (HIGHLIGHTED MAIN DATA TAB) */}
            <button
              type="button"
              onClick={() => {
                setSelectedPortalNode(null);
                setActiveTab('portal_data');
                setMobileNavOpen(false);
              }}
              className={`w-full px-3 py-2.5 rounded-xl text-xs font-black transition-all flex items-center justify-between cursor-pointer border relative overflow-hidden ${
                activeTab === 'portal_data' && selectedPortalNode !== 'whatsapp_web'
                  ? 'bg-gradient-to-r from-cyan-500/25 via-blue-500/20 to-teal-500/20 border-cyan-400 text-cyan-200 shadow-[0_0_25px_rgba(6,182,212,0.45)] ring-1 ring-cyan-400/40'
                  : 'bg-gradient-to-r from-[#0c1626] to-[#0a1120] border-cyan-500/40 text-cyan-300 hover:border-cyan-400 hover:text-white shadow-sm'
              }`}
            >
              <div className="flex items-center gap-2.5 z-10">
                <Database size={15} className="text-cyan-400 animate-pulse" />
                <span className="tenet-nav-label-full tracking-wide">Portal Data</span>
                <span className="tenet-nav-label-short tracking-wide">Data</span>
              </div>
              <span className="tenet-nav-badge z-10 px-1.5 py-0.5 rounded text-[9px] font-mono font-black bg-gradient-to-r from-cyan-500 to-blue-600 text-slate-950 shadow-[0_0_10px_rgba(6,182,212,0.6)] animate-pulse">
                MAIN DATA
              </span>
            </button>

            {/* 2b. WhatsApp Web Direct Studio App (ONLY rendered if WhatsApp Instance Node is actively in flow) */}
            {hasWhatsAppNode && (
              <button
                type="button"
                onClick={() => {
                  setSelectedPortalNode('whatsapp_web');
                  setActiveTab('portal_data');
                  setMobileNavOpen(false);
                }}
                className={`w-full px-3 py-2.5 rounded-xl text-xs font-bold transition-all flex items-center justify-between cursor-pointer border relative overflow-hidden group ${
                  activeTab === 'portal_data' && selectedPortalNode === 'whatsapp_web'
                    ? 'bg-gradient-to-r from-emerald-500/25 via-teal-500/20 to-[#0B101C]/50 border-emerald-400 text-emerald-200 shadow-[0_0_25px_rgba(16,185,129,0.45)] ring-1 ring-emerald-400/40'
                    : 'bg-[#0a1510]/80 border-emerald-500/30 text-emerald-300 hover:border-emerald-400 hover:text-white shadow-sm'
                }`}
              >
                <div className="flex items-center gap-2.5 z-10">
                  <Smartphone size={15} className="text-emerald-400 group-hover:scale-110 transition-transform shrink-0" />
                  <span className="tenet-nav-label-full tracking-wide whitespace-nowrap font-bold">WhatsApp Web</span>
                  <span className="tenet-nav-label-short tracking-wide whitespace-nowrap font-bold">Chat</span>
                </div>
                <span className="tenet-nav-badge z-10 px-1.5 py-0.5 rounded text-[8.5px] font-mono font-black bg-gradient-to-r from-emerald-500 to-teal-500 text-slate-950 shadow-[0_0_8px_rgba(16,185,129,0.6)] shrink-0">
                  LIVE CHAT
                </span>
              </button>
            )}

            {/* 3. Portal Builder */}
            <button
              type="button"
              onClick={() => {
                setActiveTab('portal_builder');
                setMobileNavOpen(false);
              }}
              className={`w-full px-3 py-2.5 rounded-xl text-xs font-bold transition-all flex items-center justify-between cursor-pointer border ${
                activeTab === 'portal_builder'
                  ? 'bg-gradient-to-r from-cyan-500/20 via-cyan-500/10 to-[#0B101C]/50 border-cyan-400/80 text-cyan-200 shadow-[0_0_20px_rgba(6,182,212,0.3)] ring-1 ring-cyan-500/30'
                  : 'bg-[#0c101c]/70 border-white/[0.06] text-slate-400 hover:text-white hover:bg-white/[0.04] hover:border-white/[0.14]'
              }`}
            >
              <div className="flex items-center gap-2.5">
                <Layout size={15} className={activeTab === 'portal_builder' ? 'text-cyan-400' : 'text-slate-400'} />
                <span className="tenet-nav-label-full">Portal Builder</span>
                <span className="tenet-nav-label-short">Build</span>
              </div>
              <span className="tenet-nav-badge px-1.5 py-0.5 rounded text-[9px] font-mono font-bold bg-cyan-500/20 text-cyan-300 border border-cyan-500/30">
                DRAG & DROP
              </span>
            </button>

            {/* 4. My Account & Membership (Full-Page Studio) */}
            <button
              type="button"
              onClick={() => {
                setActiveTab('billing');
                setMobileNavOpen(false);
              }}
              className={`w-full px-3 py-2.5 rounded-xl text-xs font-bold transition-all flex items-center justify-between cursor-pointer border ${
                activeTab === 'billing'
                  ? 'bg-gradient-to-r from-amber-500/20 via-amber-500/10 to-[#0B101C]/50 border-amber-400/80 text-amber-200 shadow-[0_0_20px_rgba(245,158,11,0.3)] ring-1 ring-amber-500/30'
                  : 'bg-[#0c101c]/70 border-white/[0.06] text-slate-400 hover:text-white hover:bg-white/[0.04] hover:border-white/[0.14]'
              }`}
            >
              <div className="flex items-center gap-2.5">
                <Crown size={15} className={activeTab === 'billing' ? 'text-amber-400' : 'text-amber-500/70'} />
                <span className="tenet-nav-label-full">My Account & Plan</span>
                <span className="tenet-nav-label-short">Account</span>
              </div>
              <span className={`tenet-nav-badge text-[9px] font-mono font-bold px-1.5 py-0.5 rounded border ${
                tenant?.planType === 'pro' || tenant?.planType === 'enterprise'
                  ? 'bg-emerald-500/20 text-emerald-300 border-emerald-500/40 shadow-[0_0_8px_rgba(16,185,129,0.2)]'
                  : 'bg-amber-500/20 text-amber-300 border-amber-500/30'
              }`}>
                {tenant?.planType === 'pro' || tenant?.planType === 'enterprise' ? 'PRO' : 'STUDIO'}
              </span>
            </button>
          </div>

          {/* Flow Engine Telemetry Widget */}
          <div className="tenet-drawer-item bg-gradient-to-b from-[#111728] to-[#090D17] border border-cyan-500/25 rounded-xl p-2.5 space-y-1.5 shadow-inner">
            <div className="flex items-center gap-1.5">
              <Activity className="w-3.5 h-3.5 text-cyan-400 animate-pulse shrink-0" />
              <span className="text-[9.5px] font-bold uppercase tracking-wider text-cyan-300 font-mono">AGENT FLOW RUNTIME</span>
            </div>
            <p className="text-[10px] text-slate-300 leading-snug font-sans">
              Widgets & portal modules in this workspace are generated directly from your active <span className="text-cyan-300 font-mono font-semibold">LunaAgentFlow</span> pipeline.
            </p>
            <div className="pt-1.5 flex items-center justify-between text-[9.5px] text-slate-400 font-mono border-t border-white/[0.06]">
              <span>Runtime Engine:</span>
              <span className="text-emerald-400 font-semibold truncate">Luna Multimodal Core</span>
            </div>
          </div>
        </div>

        {/* Sidebar Footer Controls & API Key Card */}
        <div className="tenet-drawer-item space-y-2.5 pt-3.5 border-t border-white/[0.08] shrink-0">
          
          {/* System Release & Version Status Card (Enterprise SaaS Standard) */}
          <div 
            onClick={() => {
              setShowVersionModal(true);
              setMobileNavOpen(false);
            }}
            className="bg-gradient-to-b from-[#111728] to-[#090D17] hover:bg-[#151D33] border border-cyan-500/30 hover:border-cyan-400/60 rounded-xl p-2.5 space-y-1.5 transition cursor-pointer group shadow-sm"
          >
            <div className="flex items-center justify-between">
              <span className="text-[10px] font-mono text-cyan-300 font-bold flex items-center gap-1.5">
                <Sparkles size={11} className="text-cyan-400 group-hover:rotate-12 transition-transform" />
                <span>RELEASE VERSION</span>
              </span>
              <span className={`text-[9px] font-mono font-bold px-1.5 py-0.5 rounded border ${
                systemRelease?.isBeta
                  ? 'bg-amber-500/20 text-amber-300 border-amber-500/30'
                  : 'bg-emerald-500/20 text-emerald-300 border-emerald-500/30 shadow-[0_0_8px_rgba(16,185,129,0.2)]'
              }`}>
                {systemRelease?.isBeta ? 'CANARY BETA' : 'STABLE'}
              </span>
            </div>

            <div className="flex items-center justify-between pt-0.5">
              <div className="flex items-baseline gap-1.5">
                <span className="text-xs font-black font-mono text-white group-hover:text-cyan-200 transition-colors">
                  {runningVersion || systemRelease?.version || 'v6.11.3'}
                </span>
                <span className="text-[9px] text-slate-500 font-sans">
                  {systemRelease?.releasedAt ? new Date(systemRelease.releasedAt).toLocaleDateString() : 'Live'}
                </span>
              </div>

              {isUpdateAvailable ? (
                <span className="text-[9px] font-mono font-black text-amber-300 bg-amber-500/20 px-1.5 py-0.5 rounded-full border border-amber-500/40 flex items-center gap-1 animate-pulse">
                  <RefreshCw size={9} className="animate-spin" /> UPDATE
                </span>
              ) : (
                <span className="text-[9px] font-mono text-slate-400 flex items-center gap-0.5 opacity-60 group-hover:opacity-100 transition">
                  <span>Changelog</span> <ChevronRight size={10} />
                </span>
              )}
            </div>
          </div>

          {/* API Key Display & Copy Action */}
          <div className="bg-gradient-to-b from-[#111728] to-[#090D17] border border-white/[0.08] rounded-xl p-2.5 space-y-1">
            <div className="flex items-center justify-between">
              <span className="text-[10px] font-mono text-slate-400 uppercase font-bold flex items-center gap-1">
                <Shield size={11} className="text-cyan-400" /> TENET API KEY
              </span>
              <button
                type="button"
                onClick={() => copyToClipboard(tenant?.apiKey || '', 'API Key')}
                className="text-[10px] text-cyan-400 hover:text-cyan-300 font-bold transition flex items-center gap-1 cursor-pointer font-mono"
                title="Copy API Key"
              >
                {copiedKey ? <Check size={11} className="text-emerald-400" /> : <Copy size={11} />}
                <span>{copiedKey ? 'COPIED' : 'COPY'}</span>
              </button>
            </div>
            <div className="text-[10px] font-mono text-slate-300 bg-slate-950/80 px-2 py-1 rounded border border-white/[0.08] truncate select-all">
              {tenant?.apiKey ? `${tenant.apiKey.slice(0, 14)}...` : 'TENET_ACTIVE_SESSION'}
            </div>
          </div>

          {/* Logout Action Button */}
          <button
            onClick={onLogout}
            className="w-full px-3 py-2 rounded-xl text-xs font-sans font-semibold tracking-wide bg-rose-500/10 hover:bg-rose-500/20 border border-rose-500/30 text-rose-300 hover:text-rose-200 transition flex items-center justify-center gap-2 cursor-pointer shadow-sm active:scale-95"
          >
            <LogOut size={13} /> Logout
          </button>
        </div>
      </aside>

      {/* Mobile Drawer Backdrop */}
      {mobileNavOpen && (
        <div
          onClick={() => setMobileNavOpen(false)}
          className="fixed inset-0 z-20 bg-black/60 backdrop-blur-sm md:hidden animate-in fade-in duration-200"
        />
      )}

      {/* ═════════════════════════════════════════════════════════════════ */}
      {/* FULL-BLEED WORKSPACE CANVAS / PORTAL DATA / PORTAL BUILDER        */}
      {/* ═════════════════════════════════════════════════════════════════ */}
      <main className="tenet-shell-main flex-1 min-h-0 h-full relative overflow-hidden bg-[#07090E] w-full flex flex-col">
        {activeTab === 'workspace' ? (
          <TenetDynamicWorkspace
            token={token}
            tenant={tenant}
            onOpenPortalData={(nodeType, nodeId) => {
              setSelectedPortalNode(nodeType || 'lunaCoreNode');
              setSelectedPortalNodeId(nodeId || null);
              setActiveTab('portal_data');
            }}
            onOpenAccountModal={(tab) => {
              if (tab) setBillingInitialTab(tab as any);
              setActiveTab('billing');
            }}
          />
        ) : activeTab === 'portal_data' ? (
          <TenetPortalData
            token={token}
            tenant={tenant}
            initialNode={selectedPortalNode}
            initialNodeId={selectedPortalNodeId}
            onBackToWorkspace={() => setActiveTab('workspace')}
            onOpenAccountModal={(tab) => {
              if (tab) setBillingInitialTab(tab as any);
              setActiveTab('billing');
            }}
          />
        ) : activeTab === 'portal_builder' ? (
          <TenetPortalBuilder token={token} tenant={tenant} />
        ) : (
          <TenetAccountBillingModal
            isOpen={true}
            isFullPage={true}
            onClose={() => setActiveTab('workspace')}
            token={token}
            tenantApiKey={tenant?.apiKey}
            initialTenant={tenant}
            initialTab={billingInitialTab}
            onPlanUpdated={(updated) => setTenant(updated)}
          />
        )}
      </main>

      {/* ═════════════════════════════════════════════════════════════════ */}
      {/* TENET SYSTEM VERSION & RELEASE GOVERNANCE MODAL                   */}
      {/* ═════════════════════════════════════════════════════════════════ */}
      {showVersionModal && (
        <div className="fixed inset-0 z-[99999] bg-black/80 backdrop-blur-md flex items-center justify-center p-4">
          <div className="bg-gradient-to-b from-[#080B14]/98 via-[#0C1222]/98 to-[#06080F]/98 border border-white/[0.12] rounded-3xl w-full max-w-xl max-h-[85vh] overflow-hidden flex flex-col shadow-[0_25px_80px_rgba(0,0,0,0.95),inset_0_1px_0_rgba(255,255,255,0.1)] animate-in fade-in zoom-in-95 duration-200">
            {/* Modal Header */}
            <div className="px-5 py-4 border-b border-white/[0.08] flex items-center justify-between bg-slate-950/60">
              <div className="flex items-center gap-2.5">
                <div className="p-2 rounded-xl bg-cyan-500/15 border border-cyan-500/30 text-cyan-400 shadow-[0_0_15px_rgba(6,182,212,0.2)]">
                  <Sparkles size={16} />
                </div>
                <div>
                  <h3 className="text-sm font-bold text-white flex items-center gap-2">
                    <span>Kangavena Release Governance</span>
                    <span className="text-[10px] font-mono font-bold px-2 py-0.5 rounded-full bg-cyan-500/20 text-cyan-300 border border-cyan-500/30">
                      ENTERPRISE SAAS
                    </span>
                  </h3>
                  <p className="text-[11px] text-slate-400">
                    Active Tenant Version, Changelog & Instant Checkpoint Rollbacks
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setShowVersionModal(false)}
                className="text-slate-400 hover:text-white p-1.5 rounded-lg hover:bg-slate-800 transition cursor-pointer"
              >
                <X size={16} />
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-5 overflow-y-auto space-y-4 flex-1">
              
              {/* If Update Available Banner */}
              {isUpdateAvailable && (
                <div className="p-4 rounded-xl border border-amber-500/40 bg-amber-950/20 shadow-[0_0_30px_rgba(245,158,11,0.15)] space-y-2.5">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="flex h-2.5 w-2.5 relative">
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-75" />
                        <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-amber-500" />
                      </span>
                      <span className="text-xs font-bold text-amber-300 font-mono uppercase tracking-wide">
                        NEW VERSION READY TO APPLY
                      </span>
                    </div>
                    <span className="text-xs font-black font-mono text-white bg-amber-500/30 px-2 py-0.5 rounded border border-amber-500/40">
                      {systemRelease?.version}
                    </span>
                  </div>
                  <h4 className="text-xs font-bold text-white">
                    {systemRelease?.title}
                  </h4>
                  {systemRelease?.releaseNotes && (
                    <div className="p-2.5 rounded-lg bg-slate-950/60 border border-amber-500/20 text-[11px] font-mono text-slate-300 whitespace-pre-wrap max-h-32 overflow-y-auto leading-relaxed">
                      {systemRelease.releaseNotes}
                    </div>
                  )}
                  <button
                    type="button"
                    onClick={() => handleApplyUpdate(systemRelease.version)}
                    disabled={isUpdatingRelease}
                    className="w-full py-2.5 rounded-xl bg-gradient-to-r from-cyan-500 to-emerald-500 hover:from-cyan-400 hover:to-emerald-400 text-slate-950 font-bold text-xs shadow-[0_0_20px_rgba(6,182,212,0.4)] transition flex items-center justify-center gap-2 cursor-pointer disabled:opacity-50"
                  >
                    <RefreshCw size={13} className={isUpdatingRelease ? 'animate-spin' : ''} />
                    <span>{isUpdatingRelease ? 'Applying Update...' : `Update Now to ${systemRelease.version} (Zero-Downtime)`}</span>
                  </button>
                </div>
              )}

              {/* Current Active Release Card */}
              <div className="p-4 rounded-xl border border-cyan-500/20 bg-gradient-to-b from-[#131A2B] to-[#0D121F] space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-[10px] font-mono text-cyan-300 font-bold uppercase tracking-wider flex items-center gap-1.5">
                    <ShieldCheck size={13} className="text-emerald-400" /> ACTIVE RUNNING VERSION
                  </span>
                  <span className="text-xs font-black font-mono text-emerald-300 bg-emerald-500/15 px-2.5 py-0.5 rounded-full border border-emerald-500/30">
                    {runningVersion}
                  </span>
                </div>
                <div className="text-sm font-bold text-white">
                  {runningVersion === systemRelease?.version
                    ? systemRelease?.title
                    : releaseHistory.find(r => r.version === runningVersion)?.title || 'Current Tenant Release'}
                </div>
                <div className="p-3 rounded-lg bg-slate-950/60 border border-slate-800 text-[11px] font-mono text-slate-300 whitespace-pre-wrap max-h-40 overflow-y-auto leading-relaxed">
                  {(runningVersion === systemRelease?.version
                    ? systemRelease?.releaseNotes
                    : releaseHistory.find(r => r.version === runningVersion)?.releaseNotes) ||
                    '• Zero-downtime rolling runtime active with Luna Multimodal AI pipeline.'}
                </div>
              </div>

              {/* Rollback Timeline & Release History */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <h4 className="text-xs font-bold text-slate-300 flex items-center gap-1.5">
                    <History size={13} className="text-cyan-400" />
                    <span>Rollback Checkpoints & Release History</span>
                  </h4>
                  <span className="text-[10px] font-mono text-slate-500">
                    Sub-10ms Instant Switch
                  </span>
                </div>

                <div className="space-y-1.5">
                  {releaseHistory.length === 0 ? (
                    <div className="p-3 rounded-lg bg-slate-900/40 border border-slate-800 text-center text-xs text-slate-400">
                      No prior release checkpoints recorded.
                    </div>
                  ) : (
                    releaseHistory.map((rel: any) => {
                      const cmp = compareSemver(rel.version, runningVersion);
                      const isCurrent = cmp === 0;
                      const isUpgrade = cmp > 0;
                      const isExpanded = expandedReleaseId === (rel.id || rel.version);

                      return (
                        <div
                          key={rel.id || rel.version}
                          className={`p-3 rounded-xl border transition ${
                            isCurrent
                              ? 'bg-emerald-950/20 border-emerald-500/30 text-emerald-200'
                              : 'bg-slate-900/50 border-slate-800 hover:border-slate-700 text-slate-300'
                          }`}
                        >
                          <div className="flex items-center justify-between gap-3">
                            <div className="min-w-0 flex-1">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="text-xs font-mono font-bold text-white">
                                  {rel.version}
                                </span>
                                <span className={`text-[9px] font-mono px-1.5 py-0.5 rounded border ${
                                  rel.status === 'draft_beta'
                                    ? 'bg-amber-500/20 text-amber-300 border-amber-500/30'
                                    : rel.status === 'active_production'
                                    ? 'bg-emerald-500/20 text-emerald-300 border-emerald-500/30'
                                    : 'bg-slate-800 text-slate-400 border-slate-700'
                                }`}>
                                  {rel.status === 'draft_beta' ? 'BETA' : rel.status === 'active_production' ? 'PRODUCTION' : 'ARCHIVED'}
                                </span>
                                {rel.releasedAt && (
                                  <span className="text-[9px] text-slate-500 font-mono">
                                    {new Date(rel.releasedAt).toLocaleDateString()}
                                  </span>
                                )}
                              </div>
                              <div className="text-[11px] text-slate-300 font-medium mt-1">
                                {rel.title}
                              </div>
                              {rel.releaseNotes && (
                                <button
                                  type="button"
                                  onClick={() => setExpandedReleaseId(isExpanded ? null : (rel.id || rel.version))}
                                  className="mt-1 flex items-center gap-1 text-[10px] text-cyan-400/80 hover:text-cyan-300 font-mono transition cursor-pointer"
                                >
                                  <span>{isExpanded ? 'Hide Features & Changelog' : 'View Features & Changelog'}</span>
                                  {isExpanded ? <ChevronUp size={11} /> : <ChevronDown size={11} />}
                                </button>
                              )}
                            </div>

                            {isCurrent ? (
                              <span className="px-2.5 py-1 rounded-lg text-[10px] font-mono font-bold bg-emerald-500/20 text-emerald-300 border border-emerald-500/30 shrink-0 flex items-center gap-1">
                                <CheckCircle2 size={11} /> ACTIVE
                              </span>
                            ) : isUpgrade ? (
                              <button
                                type="button"
                                onClick={() => handleApplyUpdate(rel.version)}
                                disabled={isUpdatingRelease}
                                className="px-2.5 py-1 rounded-lg text-[10px] font-mono font-bold bg-gradient-to-r from-cyan-500 to-emerald-500 hover:from-cyan-400 hover:to-emerald-400 text-slate-950 transition flex items-center gap-1 shrink-0 cursor-pointer shadow-[0_0_12px_rgba(6,182,212,0.3)] disabled:opacity-50"
                              >
                                <ArrowUpCircle size={11} />
                                <span>Update</span>
                              </button>
                            ) : (
                              <button
                                type="button"
                                onClick={() => handleRollbackVersion(rel.version)}
                                className="px-2.5 py-1 rounded-lg text-[10px] font-mono font-bold bg-slate-800 hover:bg-slate-700 text-amber-300 hover:text-amber-200 border border-amber-500/30 transition flex items-center gap-1 shrink-0 cursor-pointer shadow-sm"
                              >
                                <RotateCcw size={10} />
                                <span>Rollback</span>
                              </button>
                            )}
                          </div>

                          {/* Expandable Release Notes and Feature List */}
                          {isExpanded && rel.releaseNotes && (
                            <div className="mt-2.5 p-3 rounded-lg bg-slate-950/80 border border-slate-800 text-[11px] font-mono text-slate-300 whitespace-pre-wrap leading-relaxed shadow-inner max-h-48 overflow-y-auto">
                              <div className="text-[10px] font-bold text-cyan-400 mb-1 flex items-center gap-1">
                                <Sparkles size={11} /> Included Features & Changelog:
                              </div>
                              {rel.releaseNotes}
                            </div>
                          )}
                        </div>
                      );
                    })
                  )}
                </div>
              </div>

            </div>

            {/* Modal Footer */}
            <div className="px-5 py-3 border-t border-slate-800/80 bg-slate-950/60 flex items-center justify-between text-[10px] font-mono text-slate-500">
              <span className="flex items-center gap-1 text-slate-400">
                <ShieldCheck size={12} className="text-emerald-400" />
                Zero-Downtime Safe Atomic Builds (Rule 23 Compliant)
              </span>
              <button
                type="button"
                onClick={() => setShowVersionModal(false)}
                className="px-3 py-1 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-300 text-xs transition cursor-pointer"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ✅ Phase 5: quick switch back to Simple Mode */}
      <button
        onClick={() => switchUiMode('simple')}
        style={{ position: 'fixed', left: 18, bottom: 76, zIndex: 60, width: 52, height: 52, borderRadius: '50%', border: '1px solid rgba(125,211,252,0.45)', background: 'linear-gradient(140deg, rgba(56,189,248,0.92), rgba(37,99,235,0.92))', color: '#fff', fontSize: 20, cursor: 'pointer', boxShadow: '0 12px 28px rgba(56,189,248,0.4)' }}
        title="Simple Mode"
      >🌙</button>

      {/* ✅ Phase 1: Approvals — Liquid Glass floating panel (mobile + desktop) */}
      <div style={{ position: 'fixed', right: 18, bottom: 76, zIndex: 60 }}>
        {approvalsResult && (
          <div style={{ marginBottom: 10, padding: '10px 14px', borderRadius: 14, background: 'rgba(10, 22, 40, 0.92)', border: '1px solid rgba(56, 189, 248, 0.4)', color: '#E2F2FF', fontSize: 13, maxWidth: 340, boxShadow: '0 12px 32px rgba(2, 8, 30, 0.5)' }}>{approvalsResult}</div>
        )}
        {showApprovals && (
          <div style={{ width: 340, maxWidth: 'calc(100vw - 36px)', maxHeight: 460, overflowY: 'auto', marginBottom: 10, borderRadius: 20, padding: 16, background: 'rgba(10, 22, 40, 0.82)', backdropFilter: 'blur(18px)', WebkitBackdropFilter: 'blur(18px)', border: '1px solid rgba(56, 189, 248, 0.35)', boxShadow: '0 18px 50px rgba(2, 8, 30, 0.55)', color: '#E2F2FF' }}>
            <div style={{ fontWeight: 800, marginBottom: 10, letterSpacing: 0.3 }}>✅ Approvals — 2-Step Confirmation</div>
            {pendingActions.length === 0 && recentActions.length === 0 && (
              <div style={{ opacity: 0.7, fontSize: 13 }}>කිසිම pending වැඩක් නෑ.</div>
            )}
            {pendingActions.map((a) => (
              <div key={a.id} style={{ marginBottom: 10, padding: 12, borderRadius: 14, background: 'rgba(56, 189, 248, 0.12)', border: '1px solid rgba(56, 189, 248, 0.3)' }}>
                <div style={{ whiteSpace: 'pre-wrap', fontSize: 12.5, lineHeight: 1.5 }}>{a.previewText}</div>
                <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                  <button onClick={() => runApprovalAction(a.id, 'confirm')} style={{ flex: 1, padding: '8px 0', borderRadius: 12, border: 'none', background: 'linear-gradient(135deg, #38BDF8, #2563EB)', color: '#fff', fontWeight: 700, cursor: 'pointer' }}>✅ Confirm</button>
                  <button onClick={() => runApprovalAction(a.id, 'cancel')} style={{ flex: 1, padding: '8px 0', borderRadius: 12, border: '1px solid rgba(255,255,255,0.25)', background: 'rgba(255,255,255,0.08)', color: '#FFD6D6', fontWeight: 700, cursor: 'pointer' }}>❌ Cancel</button>
                </div>
              </div>
            ))}
            {recentActions.length > 0 && (
              <div style={{ opacity: 0.6, fontSize: 11, margin: '10px 0 6px', textTransform: 'uppercase', letterSpacing: 1 }}>Recent</div>
            )}
            {recentActions.map((a) => (
              <div key={'r' + a.id} style={{ marginBottom: 6, padding: '8px 10px', borderRadius: 10, background: 'rgba(255,255,255,0.05)', fontSize: 11.5, opacity: 0.85, display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontWeight: 700, color: a.status === 'executed' ? '#7DD3FC' : a.status === 'cancelled' ? '#FCA5A5' : '#C4B5FD' }}>{a.status}</span>
                <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{a.actionKind} · {a.toolName}</span>
                {a.status === 'executed' && (
                  <button onClick={() => runApprovalAction(a.id, 'undo')} style={{ padding: '2px 10px', borderRadius: 8, border: '1px solid rgba(56,189,248,0.5)', background: 'transparent', color: '#7DD3FC', cursor: 'pointer' }}>↩ Undo</button>
                )}
              </div>
            ))}
          </div>
        )}
        <button
          onClick={() => setShowApprovals((v) => !v)}
          style={{ width: 58, height: 58, borderRadius: '50%', border: '1px solid rgba(56, 189, 248, 0.5)', background: 'linear-gradient(135deg, rgba(56,189,248,0.95), rgba(37,99,235,0.95))', color: '#fff', fontSize: 22, cursor: 'pointer', boxShadow: '0 12px 30px rgba(56,189,248,0.45)', position: 'relative' }}
          title="Approvals — 2-Step Confirmation"
        >
          ✔
          {pendingActions.length > 0 && (
            <span style={{ position: 'absolute', top: -4, right: -4, background: '#F59E0B', color: '#fff', borderRadius: '50%', width: 22, height: 22, fontSize: 12, fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{pendingActions.length}</span>
          )}
        </button>
      </div>

      {/* Tenant-Isolated Floating Cyber HUD Version Notifier */}
      <VersionUpdateNotifier tenantId={activeTenantId} token={activeToken} />

      {/* Tenet Account & Subscription Billing Modal (Fallback for non-full-page modal invocations) */}
      {isBillingModalOpen && activeTab !== 'billing' && (
        <TenetAccountBillingModal
          isOpen={isBillingModalOpen}
          isFullPage={false}
          onClose={() => setIsBillingModalOpen(false)}
          token={token}
          tenantApiKey={tenant?.apiKey}
          initialTenant={tenant}
          initialTab={billingInitialTab}
          onPlanUpdated={(updated) => setTenant(updated)}
        />
      )}
    </div>
    </>
  );
}
